import { Plus } from 'lucide-react';
import { type FocusEvent, forwardRef } from 'react';
import { Button } from 'v5/design-system/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { ListAddManageItem, ListAddTemplateItems } from './ListAddButton';
import type { UnitAddAction } from './UnitEditor';

/**
 * The “+” beside a unit's pencil. With templates (rate cards) it opens a
 * menu: the blank entry first, then the templates; otherwise it adds at once.
 */
export const UnitAddButton = forwardRef<
  HTMLButtonElement,
  {
    add: UnitAddAction;
    onAdd(templateId: string | null): void;
    onFocus(event: FocusEvent<HTMLButtonElement>): void;
  }
>(({ add, onAdd, onFocus }, ref) => {
  const button = (
    <Button
      ref={ref}
      variant="ghost"
      size="sm"
      layout="icon"
      onFocus={onFocus}
      aria-label={add.label}
      {...(add.menu ? {} : { onClick: () => onAdd(null) })}
    >
      <Plus />
    </Button>
  );
  if (!add.menu) {
    return (
      <SimpleTooltip title={add.label} align="end">
        {button}
      </SimpleTooltip>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-80 w-80 max-w-[calc(100vw-2rem)] overflow-y-auto"
      >
        <DropdownMenuItem onSelect={() => onAdd(null)} className="py-2">
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate font-semibold text-fg-default">
              {add.menu.blank.label}
            </span>
            <span className="text-sm text-fg-muted">
              {add.menu.blank.description}
            </span>
          </span>
        </DropdownMenuItem>
        {add.menu.templates.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <ListAddTemplateItems
              options={add.menu.templates}
              usedOptions={add.menu.usedTemplates}
              onAdd={onAdd}
            />
          </>
        )}
        <ListAddManageItem manage={add.menu.manage} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
UnitAddButton.displayName = 'UnitAddButton';
